import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, LayoutDashboard } from "lucide-react";
import { usePlatformConfig, useAuthState } from "@/hooks/usePlatformConfig";
import PlatformLogo from "@/components/PlatformLogo";
import ThemeToggle from "@/components/ThemeToggle";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { config, loading: configLoading } = usePlatformConfig();
  const { user, profile, loading: authLoading } = useAuthState();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = config.nav_links.filter(l => l.is_active);
  const firstName = profile?.full_name?.split(" ")[0] || user?.email?.split("@")[0] || "Usuário";

  const handleNavClick = useCallback((href: string) => {
    setOpen(false);
    if (href.startsWith("/#")) {
      const hash = href.slice(1);
      if (location.pathname === "/") {
        const el = document.querySelector(hash);
        if (el) { el.scrollIntoView({ behavior: "smooth" }); return; }
      } else {
        navigate("/");
        setTimeout(() => {
          const el = document.querySelector(hash);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 300);
        return;
      }
    }
    navigate(href);
  }, [location.pathname, navigate]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? "bg-background/95 backdrop-blur-xl shadow-sm border-b border-border" : "bg-transparent"}`}>
      <div className="container flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2.5">
          {configLoading ? (
            <div className="w-9 h-9 rounded-xl bg-muted animate-pulse shrink-0" />
          ) : (
            <PlatformLogo
              src={config.logo_url}
              alt={config.logo_text}
              fallbackInitials={config.logo_initials}
              fallbackText={config.logo_text}
            />
          )}
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => handleNavClick(l.href)}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors bg-transparent border-none cursor-pointer font-medium"
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="hidden md:flex items-center gap-2">
          <ThemeToggle collapsed />
          {!authLoading && user ? (
            <Button asChild size="sm" className="bg-gradient-primary text-primary-foreground hover:opacity-90 shadow-glow">
              <Link to="/dashboard" className="flex items-center gap-2">
                <LayoutDashboard className="h-4 w-4" />
                Olá, {firstName}
              </Link>
            </Button>
          ) : (
            <>
              <Button asChild variant="ghost" size="sm">
                <Link to="/login">Entrar</Link>
              </Button>
              <Button asChild size="sm" className="bg-gradient-primary text-primary-foreground hover:opacity-90 shadow-glow">
                <Link to="/cadastro">Começar Grátis</Link>
              </Button>
            </>
          )}
        </div>

        {/* Mobile */}
        <div className="md:hidden flex items-center gap-1">
          <ThemeToggle collapsed />
          <button className="p-2" onClick={() => setOpen(!open)}>
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-border bg-background/95 backdrop-blur-xl p-5 space-y-1">
          {links.map((l) => (
            <button
              key={l.href}
              onClick={() => handleNavClick(l.href)}
              className="block w-full text-left text-sm text-muted-foreground py-3 px-2 rounded-lg hover:bg-secondary transition-colors bg-transparent border-none cursor-pointer"
            >
              {l.label}
            </button>
          ))}
          <div className="flex gap-2 pt-4">
            {!authLoading && user ? (
              <Button asChild size="sm" className="flex-1 bg-gradient-primary text-primary-foreground shadow-glow">
                <Link to="/dashboard" className="flex items-center gap-2">
                  <LayoutDashboard className="h-4 w-4" />
                  Olá, {firstName}
                </Link>
              </Button>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" className="flex-1">
                  <Link to="/login">Entrar</Link>
                </Button>
                <Button asChild size="sm" className="flex-1 bg-gradient-primary text-primary-foreground shadow-glow">
                  <Link to="/cadastro">Começar Grátis</Link>
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
